import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Sword, 
  Zap, 
  Heart, 
  Smile, 
  Sparkles, 
  Gamepad2, 
  Music,
  Brain,
  Ghost,
  Rocket,
  Coffee,
  Trophy,
  Eye,
  Skull
} from 'lucide-react'

const GENRE_DATA = [
  {
    name: 'Action',
    icon: Sword,
    description: 'High-energy anime with intense battles and thrilling sequences',
    color: 'from-red-500 to-orange-500',
    count: '2,847'
  },
  {
    name: 'Adventure',
    icon: Zap,
    description: 'Epic journeys and exciting quests in fantastic worlds',
    color: 'from-blue-500 to-cyan-500',
    count: '1,923'
  },
  {
    name: 'Romance',
    icon: Heart,
    description: 'Love stories that will make your heart skip a beat',
    color: 'from-pink-500 to-rose-500',
    count: '1,456'
  },
  {
    name: 'Comedy',
    icon: Smile,
    description: 'Hilarious anime that will keep you laughing',
    color: 'from-yellow-500 to-amber-500',
    count: '2,134'
  },
  {
    name: 'Fantasy',
    icon: Sparkles,
    description: 'Magical worlds filled with wonder and mystery',
    color: 'from-purple-500 to-indigo-500',
    count: '1,789'
  },
  {
    name: 'Sci-Fi',
    icon: Rocket,
    description: 'Futuristic stories with advanced technology',
    color: 'from-green-500 to-teal-500',
    count: '1,234'
  },
  {
    name: 'Sports',
    icon: Trophy,
    description: 'Competitive sports anime with inspiring stories',
    color: 'from-orange-500 to-red-500',
    count: '567'
  },
  {
    name: 'Slice of Life',
    icon: Coffee,
    description: 'Peaceful everyday stories about ordinary life',
    color: 'from-green-400 to-blue-400',
    count: '1,890'
  },
  {
    name: 'Psychological',
    icon: Brain,
    description: 'Mind-bending stories that challenge your perception',
    color: 'from-gray-600 to-gray-800',
    count: '456'
  },
  {
    name: 'Horror',
    icon: Ghost,
    description: 'Spine-chilling anime for thrill seekers',
    color: 'from-gray-800 to-black',
    count: '234'
  },
  {
    name: 'Mystery',
    icon: Eye,
    description: 'Intriguing puzzles and detective stories',
    color: 'from-indigo-600 to-purple-600',
    count: '789'
  },
  {
    name: 'Thriller',
    icon: Skull,
    description: 'Suspenseful anime that keep you on edge',
    color: 'from-red-600 to-gray-800',
    count: '345'
  },
  {
    name: 'Music',
    icon: Music,
    description: 'Anime celebrating the power of music and performance',
    color: 'from-pink-400 to-purple-400',
    count: '123'
  },
  {
    name: 'Mecha',
    icon: Gamepad2,
    description: 'Giant robots and mechanical warfare',
    color: 'from-gray-500 to-blue-600',
    count: '456'
  }
]

export default function Genres() {
  const [hoveredGenre, setHoveredGenre] = useState<string | null>(null)

  return (
    <div className="min-h-screen bg-dark-400 py-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white mb-4">
            Explore by Genre
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Discover anime that match your mood and preferences. From action-packed adventures to heartwarming slice-of-life stories.
          </p>
        </div>

        {/* Genre Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {GENRE_DATA.map((genre) => {
            const IconComponent = genre.icon
            return (
              <Link
                key={genre.name}
                to={`/genre/${genre.name.toLowerCase()}`}
                className="group relative overflow-hidden"
                onMouseEnter={() => setHoveredGenre(genre.name)}
                onMouseLeave={() => setHoveredGenre(null)}
              >
                <div className={`
                  relative p-8 rounded-xl bg-gradient-to-br ${genre.color} 
                  transform transition-all duration-300 group-hover:scale-105 
                  group-hover:shadow-2xl group-hover:shadow-blue-500/20
                  min-h-[200px] flex flex-col justify-between
                `}>
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-4 right-4 w-20 h-20 rounded-full border-2 border-white/30" />
                    <div className="absolute bottom-4 left-4 w-12 h-12 rounded-full border border-white/20" />
                    <div className="absolute top-1/2 left-1/2 w-6 h-6 rounded-full bg-white/20 transform -translate-x-1/2 -translate-y-1/2" />
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-4">
                      <IconComponent className="w-10 h-10 text-white" />
                      <span className="text-white/80 text-sm font-medium">
                        {genre.count}
                      </span>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {genre.name}
                    </h3>
                    
                    <p className="text-white/90 text-sm leading-relaxed">
                      {genre.description}
                    </p>
                  </div>

                  {/* Hover Effect */}
                  <div className={`
                    absolute inset-0 bg-gradient-to-t from-black/20 to-transparent 
                    opacity-0 group-hover:opacity-100 transition-opacity duration-300
                  `} />
                  
                  {/* Animated Border */}
                  <div className={`
                    absolute inset-0 rounded-xl border-2 border-transparent
                    ${hoveredGenre === genre.name ? 'border-white/50' : ''}
                    transition-all duration-300
                  `} />
                </div>

                {/* Floating Action Indicator */}
                <div className={`
                  absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-full 
                  flex items-center justify-center backdrop-blur-sm
                  transform transition-all duration-300
                  ${hoveredGenre === genre.name ? 'scale-110 bg-white/30' : 'scale-100'}
                `}>
                  <div className="w-2 h-2 bg-white rounded-full" />
                </div>
              </Link>
            )
          })}
        </div>

        {/* Popular Combinations */}
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">
            Popular Combinations
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { genres: ['Action', 'Adventure'], count: '1,234' },
              { genres: ['Romance', 'Comedy'], count: '987' },
              { genres: ['Fantasy', 'Adventure'], count: '756' },
              { genres: ['Sci-Fi', 'Action'], count: '654' },
              { genres: ['Slice of Life', 'Comedy'], count: '543' },
              { genres: ['Mystery', 'Psychological'], count: '432' }
            ].map((combo, index) => (
              <Link
                key={index}
                to={`/browse?genres=${combo.genres.join(',').toLowerCase()}`}
                className="group p-6 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors border border-gray-700 hover:border-gray-600"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex space-x-2">
                    {combo.genres.map((genre, i) => (
                      <span key={i} className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full">
                        {genre}
                      </span>
                    ))}
                  </div>
                  <span className="text-gray-400 text-sm">{combo.count}</span>
                </div>
                <p className="text-gray-300 text-sm group-hover:text-white transition-colors">
                  Explore anime that combine {combo.genres.join(' and ').toLowerCase()}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
